import { getBasicFormatters } from '@foresightdata/web-common-formatter-service';
import { atom } from 'jotai';
import { RendererType } from '../../data-models/field-definition';
import { PrimitiveType } from '../../data-models/field3.data-model';
import { IFormatterDataModel } from '../../data-models/formatter.data-model';
import { customFormattersAtom } from '../../services/state/AppConfigStateJ';
import { FMT } from '../../util/formatter-service';

export const ConfigurableRendererTypeToType: Partial<Record<RendererType, PrimitiveType>> = {
  [RendererType.boolean]: 'boolean',
  [RendererType.date]: 'date',
  [RendererType.freeLabels]: 'array',
  [RendererType.integer]: 'number',
  [RendererType.multiplier]: 'number',
  [RendererType.number]: 'number',
  [RendererType.numeric]: 'number',
  [RendererType.percent]: 'number',
  [RendererType.currency]: 'number',
  // extend as needed;
  // assume rest map to string
};

/**
 * The formatter a renderer should start on — an id the picker actually offers, which is not the
 * same thing as the id the forms historically stamped.
 *
 * `number` is the whole reason this exists: `rendererToFormatterId` derives `'number'`, which is
 * not a registered formatter, so a field carrying it showed an empty Format picker. Every other
 * renderer's derived id happens to be a real one. Deliberately *not* folded into
 * `rendererToFormatterId`, which encodes what the forms stamp and is what
 * `getFieldFormatterOverride` uses to recognise a field as un-configured.
 */
const DEFAULT_FORMATTER_ID_BY_RENDERER: Partial<Record<RendererType, string>> = {
  [RendererType.boolean]: 'yesNo',
  [RendererType.currency]: 'currency',
  [RendererType.date]: 'date',
  [RendererType.freeLabels]: 'stringArray',
  [RendererType.integer]: 'integer',
  [RendererType.multiplier]: 'multiplier',
  [RendererType.number]: 'numeric',
  [RendererType.numeric]: 'numeric',
  [RendererType.percent]: 'percent',
};

export function defaultFormatterIdForRenderer(renderer: RendererType | string | undefined) {
  return DEFAULT_FORMATTER_ID_BY_RENDERER[renderer as RendererType];
}

const presetFormatterIdsAtom = atom<Set<string>>((get) => {
  const customFormatters = get(customFormattersAtom);
  return new Set([...customFormatters, ...getBasicFormatters()].map((f) => f.id));
});

export const formatterModelsByTypeAtom = atom<Map<PrimitiveType, IFormatterDataModel<unknown>[]>>((get) => {
  const presetFormatterIds = get(presetFormatterIdsAtom);
  const allFormatterModels = FMT.get().getAllFormatterModels();

  return createFormatterModelByTypeMap(allFormatterModels, presetFormatterIds);
});

export function createFormatterModelByTypeMap(
  formatterModels: IFormatterDataModel<unknown>[],
  presetFormatterIds: Set<string>
): Map<PrimitiveType, IFormatterDataModel<unknown>[]> {
  return formatterModels.reduce((acc, curr) => {
    if (!presetFormatterIds.has(curr.id)) {
      return acc;
    }
    const type =
      formatterTypeToPrimitiveType(curr.type) ??
      ConfigurableRendererTypeToType[curr.id as RendererType] ??
      ConfigurableRendererTypeToType[curr.extends as RendererType];
    if (!type) {
      return acc;
    }
    const existing = acc.get(type) ?? [];
    existing.push(curr);
    return acc.set(type, existing);
  }, new Map<PrimitiveType, IFormatterDataModel<unknown>[]>());
}

function formatterTypeToPrimitiveType(fmtType?: PrimitiveType | 'currency'): PrimitiveType | undefined {
  if (fmtType === 'currency') {
    return 'number';
  }
  return fmtType;
}
